// Self-serve account deletion: the two calls Account settings → Security makes.
//
// Both send their OWN `Authorization` header with a token fetched by
// getIdToken(true). The forced refresh is the point: the backend refuses a
// deletion unless the token's auth_time is under 10 minutes old, and the page
// re-authenticates the person (password or Google) right before deleting. A
// cached token minted before that re-auth would still carry the old sign-in
// time and be refused. The apiAuth.js interceptor leaves requests that already
// carry the header alone, so it does not swap this one for a cached token.
//
// Deliberately no try/catch, for the same reason as editAccountService in
// userService.js: the page has to tell "blocked" (409), "confirm again" (401)
// and "slow down" (429) apart, and a swallowed error would leave it with
// nothing to say.
import axios from "axios";
import { auth } from "../config/firebase-config";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const freshAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    const error = new Error("No signed-in Firebase user");
    error.code = "auth/no-current-user";
    throw error;
  }
  return { Authorization: `Bearer ${await currentUser.getIdToken(true)}` };
};

/**
 * What stands in the way of deleting this account, and how much would go.
 * Resolves to { blockers: [{ code, count?, credits? }], summary: {...} }; the
 * summary includes studioApps, studioLiveApps, studioMessagesLeft and
 * studioTrial.
 */
export const getDeleteAccountCheck = async () => {
  const response = await axios.get(API_BASE_URL + "/codenec/delete-account/check", {
    withCredentials: true,
    headers: await freshAuthHeaders(),
  });
  return response.data;
};

/**
 * Deletes the signed-in account. `locale` ("en" | "es") picks the language of
 * the confirmation email. `studioAppsConfirmed` is the number of Studio apps
 * the person typed; the backend answers 409 studio_apps_unconfirmed when it
 * is missing or no longer matches. Resolves to { deleted: true, emailSent };
 * by then the server has also ended the Express session behind the cookie.
 */
export const deleteAccount = async ({ locale, studioAppsConfirmed } = {}) => {
  const body = { locale };
  if (Number.isInteger(studioAppsConfirmed)) body.studioAppsConfirmed = studioAppsConfirmed;
  const response = await axios.post(
    API_BASE_URL + "/codenec/delete-account",
    body,
    { withCredentials: true, headers: await freshAuthHeaders() }
  );
  return response.data;
};
